import React from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './ClientTestimonial.css';
import { useQuery } from '@tanstack/react-query';
import { Shimmer } from 'react-shimmer';
import api from '../services/api';
import quotesImg from '../assets/images/quotes-img.png';

import { QuotesIconSVG } from '../components/SvgIcons';

const useReviews = () => {
  return useQuery({
    queryKey: ['reviews'],
    queryFn: async () => {
      const response = await api.get('/get-app-reviews?is_web=1');
      return response.data;
    },
    staleTime: 3 * 60 * 1000,
    cacheTime: 3 * 60 * 1000,
    retry: 3,
  });
};

const ClientTestimonial = () => {
  const { data: reviewData, isLoading, isError, error } = useReviews();

  const sliderItems =
    reviewData?.dataset?.data?.reviews?.slice(0, 3).map((review) => ({
      img: review.image_url || '',
      name: review.user_name || 'Anonymous User',
      company: 'Vidyakasam User',
      stars: Math.max(1, Math.round(review.rating) || 0),
      title: `"Great Experience on ${new Date(review.created_at || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}"`,
      testimonial: review.review || 'No review provided.',
      quoteImg: quotesImg,
    })) || [];

  const settings = {
    dots: false,
    infinite: sliderItems.length > 1,
    speed: 1000,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: sliderItems.length > 1,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    arrows: true,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 576,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const CustomPrevArrow = (props) => (
    <button
      {...props}
      className="slick-prev custom-arrow"
      aria-label="Previous"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
      </svg>
    </button>
  );

  const CustomNextArrow = (props) => (
    <button
      {...props}
      className="slick-next custom-arrow"
      aria-label="Next"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" fill="currentColor" />
      </svg>
    </button>
  );

  if (isLoading) {
    return (
      <div className="shimmer-wrapper">
        <div className="row">
          <div className="col-md-6">
            <div className="shimmer-item">
              <div className="testimonial-div">
                <div className="image-testimonial">
                  <Shimmer width={80} height={80} className="profile-image" />
                  <div className="profile-description">
                    <Shimmer width={120} height={18} className="shimmer-name" />
                    <Shimmer width={100} height={14} className="shimmer-company" />
                    <div className="star">
                      <Shimmer width={80} height={16} />
                    </div>
                  </div>
                </div>
                <div className="testimonial-description">
                  <Shimmer width={250} height={22} className="shimmer-title" />
                  <Shimmer width={300} height={50} className="shimmer-testimonial" />
                </div>
                <div className="testimonial-icon">
                  <div className="quotes-icon">
                    <Shimmer width={40} height={40} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="shimmer-item">
              <div className="testimonial-div">
                <div className="image-testimonial">
                  <Shimmer width={80} height={80} className="profile-image" />
                  <div className="profile-description">
                    <Shimmer width={120} height={18} className="shimmer-name" />
                    <Shimmer width={100} height={14} className="shimmer-company" />
                    <div className="star">
                      <Shimmer width={80} height={16} />
                    </div>
                  </div>
                </div>
                <div className="testimonial-description">
                  <Shimmer width={250} height={22} className="shimmer-title" />
                  <Shimmer width={300} height={50} className="shimmer-testimonial" />
                </div>
                <div className="testimonial-icon">
                  <div className="quotes-icon">
                    <Shimmer width={40} height={40} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return <div>Error: {error?.message}</div>;
  }

  if (!sliderItems.length) {
    return null;
  }

  return (
    <div className="testimonial-slider">
      <Slider {...settings} prevArrow={<CustomPrevArrow />} nextArrow={<CustomNextArrow />}>
        {sliderItems.map((item, index) => (
          <div className="item" key={index}>
            <div className="testimonial-div">
              <div className="image-testimonial">
                {item.img ? <img className="profile-image" src={item.img} alt={item.name} /> : null}
                <div className="profile-description">
                  <h6 className="mb-0">{item.name}</h6>
                  <p className="mb-0">{item.company}</p>
                  <div className="star">
                    {[...Array(item.stars)].map((_, i) => (
                      <span key={i}>
                        <i className="fa fa-star" aria-hidden="true"></i>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="testimonial-description">
                <h4>{item.title}</h4>
                <p className="mb-0">{item.testimonial}</p>
              </div>
              <div className="testimonial-icon">
                <div className="quotes-icon">
                  <QuotesIconSVG />
                </div>
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default ClientTestimonial;
